import {
  approveRequest,
  getRequestDetailUAM,
  rejectRequest,
  RequestDetailsForApprovalResponse,
  sendBackRequest,
  signAttestationEmail,
  cancelRequest,
} from "@api";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { State } from "@store";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { AttestationModal } from "../../../../request/steps/summary/views/summary/attestation-modal/attestation-modal";
import { Modal } from "../../../modal";
import { ApproversDetails } from "./approversDetails";
import { Buttons } from "./buttons";
import { RequestDetails } from "./requestDetails";
import { ModalHistoryUserRequest } from "./requestHistoryModal";
import { RequestorDetails } from "./requestorDetails";
import { alertService } from "@services";

const useStyles = makeStyles({
  link: {
    fontFamily: "Arial",
    color: "#0f7dca",
    cursor: "pointer",
  },
});

export interface Props {
  encryptId?: string;
}
//
export const UamDetails: React.FC<Props> = ({ encryptId }) => {
  const [detail, setDetail] = useState<RequestDetailsForApprovalResponse>(
    {} as RequestDetailsForApprovalResponse
  );

  const [current_user_name] = useSelector((state: State) => [
    state.user?.name || "",
    state.user?.email || "",
  ]);
  const [openHistory, setOpenHistory] = useState<boolean>(false);
  const [openSendBack, setOpenSendBack] = useState<boolean>(false);
  const [openApprove, setOpenApprove] = useState<boolean>(false);
  const [openReject, setOpenReject] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const [openViewProvisioners, setOpenViewProvisioners] = useState<boolean>(
    false
  );
  const [openReassign, setOpenReassign] = useState<boolean>(false);
  const [openAttestation, setOpenAttestation] = useState<boolean>(false);
  const [attestation, setAttestation] = useState<string>("");
  const [isMobileAttestation, setIsAttestationMobile] = useState<boolean>(
    false
  );
  const [attestationError, setAttestationError] = useState<Error | undefined>(
    undefined
  );
  const [isAttesting, setIsAttesting] = useState(false);
  const history = useHistory();
  const location = useLocation<{ readonlyAttestation?: boolean }>();
  const searchquery = useLocation<{ searchterm?: string, 
                                    searchcriteria?: string,
                                    searchapprove?: boolean,
                                    searchinprogress?: boolean,
                                    searchcompleted?: boolean,
                                    searchrejected?: boolean,
                                    searchdeclined?: boolean, }>();
  const classNames = useStyles();
  const params: { encryptId: string } = useParams();
  const [counter, updateCounter] = React.useState(0);

  useEffect(() => {
    (async () => {
      try {
        const details = await getRequestDetailUAM(
          !!encryptId ? encryptId : params.encryptId
        );
        setIsAttestationMobile(details.request.isMobileAttestation);
        setDetail(details);
        if (details.request.isAttestationSigned) {
          setAttestation(details.request.attestationName);
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [encryptId, isMobileAttestation, params.encryptId, counter]);

  const onAttestationAccept = (accept: boolean) => {
    if (attestation !== current_user_name) {
      setAttestationError(new Error(`Value should be "${current_user_name}"`));
      return;
    }
    setIsAttesting(true);
    signAttestationEmail(detail.request.id, accept, attestation).then(() =>
      setDetail({
        ...detail,
        request: { ...detail.request, isAttestationNeeded: false },
      })
    );
    setIsAttesting(false);
    setOpenAttestation(false);
  };

  const onCloseAttestationModal = () => {
    setOpenAttestation(false);
  };

  const onSendBack = useCallback(
    async (comments: string) => {
      const success = await sendBackRequest(detail.request?.id || 0, comments);
      return success
        ? { success }
        : { success, error: "There was an error Sending Back" };
    },
    [detail.request]
  );

  const [rejectSuccess, setRejectSuccess] = React.useState<Boolean>(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const onReject = useCallback(
    async (comments: string) => {
      setIsRejecting(true);
      const success = await rejectRequest(detail.request?.id, comments);
      if (success) {
        alertService.success({
          msg: "This request has been successfully rejected.",
        });
      } else {
        alertService.danger({ msg: "Failed to reject." });
      }
      setRejectSuccess(success);
      console.log(success);
      setIsRejecting(false);
      // if (success) {
      //   history.push(`/dashboard/admin/uam`);
      // }
      return success
        ? { success }
        : { success, error: "There was an error Rejecting" };
    },
    [detail.request]
  );

  React.useEffect(() => {
    if (rejectSuccess) {
      history.goBack();
    }
  }, [rejectSuccess]);

  const onCancel = useCallback(
    async (comments: string) => {
      const success = await cancelRequest(detail.request?.id, comments);
      return success
        ? { success }
        : { success, error: "There was an error Cancelling" };
    },
    [detail.request]
  );

  const onApproveRequest = useCallback(
    async (comments: string) => {
      let success = false;
      if (
        detail.request?.status === "CANCELLED" ||
        detail.request?.status === "DECLINED" ||
        detail.request?.status === "REJECTED"
      ) {
        return {
          success: false,
          error: "Sorry, the request has been cancelled and removed from queue",
        };
      } else if (detail.request?.status === "USER DECLINED ATTESTATION") {
        return {
          success: false,
          error: "Sorry, the attestation request has been declined by the user",
        };
      } else if (detail.request?.autoReject) {
        return {
          success: false,
          error: "Sorry, the request has been declined by the user",
        };
      } else if (detail.request?.isSendBack) {
        return {
          success: false,
          error: "Sorry, you do not have access to view the request",
        };
      } else {
        success = await approveRequest([detail.request?.id || 0], comments);
      }

      return { success };
    },
    [detail.request]
  );

  const incrementCounter = () => {
    updateCounter(counter + 1);
  };

  let content = null;
  if (!!detail.request) {
    content = (
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12}>
          <Box className={classNames.link}>
            <Typography
              variant="body1"
              className={classNames.link}
              onClick={() => history.push(`/dashboard/admin/uam`, {searchterm: searchquery.state?.searchterm, 
                                                                   searchcriteria: searchquery.state?.searchcriteria,
                                                                   searchapprove: searchquery.state?.searchapprove,
                                                                   searchinprogress: searchquery.state?.searchinprogress,
                                                                   searchcompleted: searchquery.state?.searchcompleted,
                                                                   searchrejected: searchquery.state?.searchrejected,
                                                                   searchdeclined: searchquery.state?.searchdeclined,})}
            >

              &larr; Back to List 
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" color="textSecondary">
            <Box>
              <Typography variant="body1" color="textSecondary">
                <FontAwesomeIcon icon={faFileAlt} />
              </Typography>
            </Box>
            <Box pl={1}>
              <Typography variant="body1" color="textSecondary">
                Request {detail.request?.referenceNumber}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <RequestorDetails
            requestor={{
              name: detail.request.requestor,
              location: detail.request.requestorOffice,
              phoneNumber: detail.request.requestorTelephoneNumber,
              email: detail.request.requestorEmail,
            }}
          ></RequestorDetails>
        </Grid>
        <Grid item xs={12}>
          <RequestDetails request={detail.request}></RequestDetails>
        </Grid>
        <Grid item xs={12}>
          <ApproversDetails
            approvers={detail.request?.approvers[0]}
            userEmail={detail.request?.userEmail}
            headerId={detail.request.id}
            status={detail.request?.status}
            handleUpdate={incrementCounter}
          ></ApproversDetails>
        </Grid>
        <Grid item xs={12}>
          <Buttons
            detail={detail}
            onViewHistory={() => setOpenHistory(true)}
            onSendBack={() => setOpenSendBack(true)}
            onReject={() => setOpenReject(true)}
            onApproveRequest={() => setOpenApprove(true)}
            onViewAttestation={() => {
              if (
                location.state?.readonlyAttestation ||
                detail.request.isAttestationSigned
              ) {
                setOpenAttestation(true);
              } else {
                history.push(`${location.pathname}/accept-attestation`);
              }
            }}
            onCancel={() => setOpenCancel(true)}
            onViewProvisioners={() => setOpenViewProvisioners(true)}
            onReassign={() => setOpenReassign(true)}
            isRejecting={isRejecting}
          ></Buttons>
        </Grid>
        <ModalHistoryUserRequest
          isOpen={openHistory}
          closeModal={() => setOpenHistory(false)}
          headerId={detail.request.id}
          arNumber={detail.request?.referenceNumber}
        />
        <Modal
          isOpen={openSendBack}
          title="Send Back Request"
          closeModal={() => {
            setOpenSendBack(false);
          }}
          buttonText="SEND BACK REQUEST"
          successMessage="Successfully Sent Back"
          onAction={onSendBack}
          required={true}
        ></Modal>
        <Modal
          isOpen={openApprove}
          closeModal={() => {
            setOpenApprove(false);
            history.goBack();
          }}
          buttonText="APPROVE REQUEST"
          successMessage="Successfully Approved"
          onAction={onApproveRequest}
        ></Modal>
        <Modal
          isOpen={openReject}
          title="Reject Request"
          closeModal={() => {
            setOpenReject(false);
          }}
          buttonText="REJECT REQUEST"
          successMessage="Successfully Rejected"
          onAction={onReject}
          required={true}
          isAction={isRejecting}
        ></Modal>
        <Modal
          isOpen={openCancel}
          title="Cancel Request"
          closeModal={() => {
            setOpenCancel(false);
          }}
          buttonText="CANCEL REQUEST"
          successMessage="Successfully Cancelled"
          onAction={onCancel}
          required={false}
        ></Modal>
        <AttestationModal
          error={attestationError}
          mobile={isMobileAttestation}
          selectedUserForTheRequest={detail.request.userName || ""}
          name={current_user_name}
          open={openAttestation}
          onAccept={() => onAttestationAccept(true)}
          onChange={(name: "attestation", value: string) =>
            setAttestation(value)
          }
          isAttesting={isAttesting}
          onDecline={() => onAttestationAccept(false)}
          onClose={onCloseAttestationModal}
          readonly={
            location.state?.readonlyAttestation ||
            detail.request.isAttestationSigned
          }
          status={
            detail.request?.status === "USER DECLINED ATTESTATION"
              ? "declined"
              : detail.request.isAttestationSigned
              ? "accepted"
              : ""
          }
          value={attestation}
        />
      </Grid>
    );
  }
  return content;
};
